import moment from './moment';

/**
 * Checks if gtag is available globally.
 *
 * @param {Array} args
 */
const gtag = (...args) => {
    if (!window.gtag) {
        return;
    }

    window.gtag(...args);
};

/**
 *
 * Search Events
 *
 * Phase 2
 *
 */

/**
 * Gets name of GA item category based on term and price.
 *
 * @param {string} term - term of booking - 'short' or 'long'
 * @param {number} price - price of booking
 *
 * @returns {string}
 */

const getItemCategory2 = (term, price) => {
    if (term === 'short') {
        if (price <= 1) {
            return 'short_term_low';
        }

        if (price > 10) {
            return 'short_term_high';
        }

        return 'short_term_medium';
    }

    if (term === 'long') {
        if (price <= 50) {
            return 'long_term_low';
        }

        if (price > 150) {
            return 'long_term_high';
        }

        return 'long_term_medium';
    }
};

/**
 * Finds time difference between 2 dates.
 *
 * @param {string} startDateTime - Starting date & time in DD-MM-YYYY HH:mm format
 * @param {string} [endDateTime = Now] - Comparison date & time in DD-MM-YYYY HH:mm format
 *
 * @returns {number} number of hours between 2 datetimes
 */

const getTimeDifference = function (startDateTime, endDateTime) {
    const startMoment = moment(startDateTime, 'DD-MM-YYYY HH:mm');
    let endMoment = moment();

    if (endDateTime != null) {
        endMoment = moment(endDateTime, 'DD-MM-YYYY HH:mm');
    }

    return Math.abs(endMoment.diff(startMoment, 'hours', true));
};

const isLatLng = function (testStr) {
    const latLng = new RegExp(/^([-+]?)([\d]{1,2})(((\.)(\d+)(,)))(\s*)(([-+]?)([\d]{1,3})((\.)(\d+))?)$/g);
    return latLng.test(testStr);
};

/**
 * Log when a user views a space.
 *
 * @param {object} data
 * @param {string} data.name - Space Title
 * @param {string} data.id - Space ID
 * @param {number} data.value - listing price minus any referral code discount plus service fee
 * @param {number} data.price - only the listing price
 * @param {string} data.term - Term of booking (short or long)
 */
export function googleLocationViewEvent(data) {
    gtag('event', 'view_item', {
        currency: 'GBP',
        value: data.value,
        items: [ // array of space bookings, contains all group booking
            {
                item_name: data.name,
                item_id: data.id,
                price: data.price,
                item_category: data.term,
                item_category2: getItemCategory2(data.term, data.value),
                quantity: 1, // always 1
            },
        ],
    });
}

/**
 * Log when a user performs a search.
 *
 * @param {object} data
 * @param {string} data.searchTerm - Term that has been searched for (place name, latlng, location id)
 * @param {string} [data.type=geographical] - Type of search performed (geographical|loaction_id|exact)
 * @param {string} data.term - Term of booking (short or long)
 * @param {string} data.startDateTime - When booking starts ('DD-MM-YYYY HH:MM')
 * @param {string} [data.endDateTime] - When booking ends - only for short term bookings ('DD-MM-YYYY HH:MM')
 */
export function googleLocationSearchPerformedEvent(data) {
    const result = {
        search_term: data.searchTerm,
        hours_until_booking_start: getTimeDifference(data.startDateTime),
        term: data.term,
        type: data.type || 'geographical',
    };

    if (isLatLng(data.searchTerm)) {
        result.type = 'exact';
    }

    if (data.term === 'short') {
        result.short_term_booking_length_hours = getTimeDifference(data.startDateTime, data.endDateTime);
    }

    gtag('event', 'search', result);
}

/**
 * Log when a user performs a search using an exact location.
 *
 * @param {object} data
 */
export function googleExactLocationSearchPerformedEvent(data) {
    gtag('event', 'search', {
        search_term: data.locationId,
        short_term_booking_length_hours: data.duration,
        hours_until_booking_start: data.hoursUntil,
        term: data.term,
        type: 'exact',
    });
}

/**
 * Log when a user clicks on a result on a search page.
 *
 * @param {object} data
 */
export function googleSearchResultViewedEvent(data) {
    gtag('event', 'select_item', {
        item_list_name: data.listName.location,
        currency: 'GBP',
        items: [ // array of space bookings, contains all group booking items
            {
                item_name: data.name,
                item_id: data.id,
                price: data.price,
                item_category: data.term,
                item_category2: getItemCategory2(data.term, data.price),
                index: data.index,
                quantity: 1, // always 1
            },
        ],
    });
}

/**
 *
 * Booking Events
 *
 * Phase 3
 *
 */

/**
 * Logs when a user clicks book now.
 *
 * @param {object} data
 */
export function googleBookingStarted(data) {
    gtag('event', 'begin_checkout', {
        currency: 'GBP',
        value: data.value,
        items: [ // array of space bookings, contains all group booking items
            {
                item_name: data.title,
                item_id: data.id,
                price: data.price,
                item_category: data.term,
                item_category2: getItemCategory2(data.term, data.value),
                quantity: 1, // always 1
            },
        ],
    });
}

/**
 * Log when a booking is reserved.
 *
 * @param {object} data
 */
export function googleBookingReserved(data) {
    gtag('event', 'booking_reserved', {
        currency: 'GBP',
        value: data.value,
        item_name: data.title,
        item_id: data.id,
        price: data.price,
        item_category: data.term,
        item_category2: getItemCategory2(data.term, data.value),
        quantity: 1, // always 1
    });
}

/**
 * Log when a short term booking is purchased.
 *
 * @param {object} data
 */
export function googleBookingShortTermPurchased(data) {
    gtag('event', 'purchase', {
        value: data.value,
        transaction_id: data.reservationId, //Booking/GroupBooking ID
        service_fee: data.serviceFee,
        currency: 'GBP',
        coupon: data.voucher || null,
        items: [ // array of space bookings, contains all group booking items
            {
                item_name: data.title,
                item_id: data.id,
                price: data.price,
                item_category: 'short_term',
                item_category2: getItemCategory2(data.term, data.value),
                quantity: 1, // always 1
            },
        ],
    });
}

/**
 * Log when a long term booking is purchased.
 *
 * @param {object} data
 */
export function googleBookingLongTermPurchased(data) {
    gtag('event', 'purchase', {
        value: data.value,
        transaction_id: data.reservationId, //Booking/GroupBooking ID
        service_fee: data.serviceFee,
        currency: 'GBP',
        coupon: data.voucher || null,
        items: [ // array of space bookings, contains all group booking items
            {
                item_name: data.title,
                item_id: data.id,
                price: data.price,
                item_category: 'long_term',
                item_category2: getItemCategory2(data.term, data.value),
                quantity: 1,  // always 1
            },
        ],
    });
}

/**
 * Log when a booking is refunded.
 *
 * @param {object} data
 */
export function googleBookingRefunded(data) {
    gtag('event', 'refund', {
        value: data.value,
        service_fee: data.service_fee,
        transaction_id: data.transaction_id,
        currency: data.currency,
        items: data.items.map((item) => {
            return {
                    ...item,
                    'item_category2': getItemCategory2(item.item_category, data.value),
                };
        }),
    });
}

/**
 * Log when a user's payment method is added during purchase.
 *
 * @param {object} data
 */
export function googlePaymentInfoAdded(data) {
    gtag('event', 'add_payment_info', {
        currency: 'GBP',
        coupon: data.voucher || null,
        service_fee: data.serviceFee,
        value: data.value,
        payment_type: data.paymentType,
        items: [ // array of space bookings, contains all group booking items
            {
                item_name: data.title,
                item_id: data.id,
                price: data.price,
                item_category: data.term,
                item_category2:  getItemCategory2(data.term, data.value),
                quantity: 1,  // always 1
            },
        ],
    });
}

/**
 * Log when a user logs in.
 * @param {object} data
 */
export function googleLoginEvent(data) {
    gtag('event', 'login', {
        method: data.method,
    });
}

/**
 * Log when a user registers with the site.
 * @param {object} data
 */
export function googleRegisterEvent(data) {
    gtag('event', 'sign_up', {
        method: data.method,
    });
}

/**
 * Log when a Space Owner finished register the location.
 * @param {object} data
 */
export function googleLocationPublished(data) {
    gtag('event', 'location_published', {
        item_name: data.title,
        item_id: data.id,
        terms: data.terms,
    });
}
